import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { NavbarComponent } from '../navbar/navbar.component';
import { SharedTestsComponent } from '../shared-tests/shared-tests.component';
import { AuthService } from '../service/auth/auth.service';
import {
  FriendRequestSummary,
  FriendSuggestion,
  FriendSummary,
  RestService,
  SocialSummary,
} from '../service/rest/rest.service';
import { LoggerService } from '../service/utils/logger.service';
import { filter, Subscription } from 'rxjs';

type StudyTab = 'shared' | 'friends';

@Component({
  selector: 'app-study-together',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, NavbarComponent, SharedTestsComponent],
  templateUrl: './study-together.component.html',
  styleUrl: './study-together.component.css',
})
export class StudyTogetherComponent implements OnInit, OnDestroy {
  activeTab: StudyTab = 'friends';
  socialSummary: SocialSummary | null = null;
  friends: FriendSummary[] = [];
  friendRequests: FriendRequestSummary[] = [];
  suggestions: FriendSuggestion[] = [];
  friendsLoading = false;
  findUsername = '';
  findCheckMessage = '';
  findCheckOk = false;
  findCheckLoading = false;
  findSubmitLoading = false;
  inviteLoadingUserId: number | null = null;
  private routerSub?: Subscription;

  constructor(
    private restService: RestService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.syncTabFromUrl();
        void this.normalizeActiveTab();
      });
  }

  async ngOnInit(): Promise<void> {
    this.syncTabFromUrl();
    await this.loadHubData();
    await this.normalizeActiveTab();
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  private syncTabFromUrl(): void {
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab === 'shared') {
      this.activeTab = 'shared';
    } else {
      // Default and legacy ?tab=find both land on the combined friends tab.
      this.activeTab = 'friends';
    }
  }

  async selectTab(tab: StudyTab): Promise<void> {
    if (!this.canSelectTab(tab)) {
      return;
    }
    this.activeTab = tab;
    const queryTab = tab === 'friends' ? null : tab;
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: queryTab },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    if (tab === 'friends') {
      await this.loadHubData();
    }
  }

  canSelectTab(tab: StudyTab): boolean {
    if (tab === 'shared') {
      return this.hasSharedRecords();
    }
    return true;
  }

  hasSharedRecords(): boolean {
    return this.sharedBadgeCount() > 0;
  }

  private async normalizeActiveTab(): Promise<void> {
    if (this.activeTab === 'shared' && !this.hasSharedRecords()) {
      await this.selectTab('friends');
    }
  }

  async loadHubData(): Promise<void> {
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      return;
    }
    this.friendsLoading = true;
    try {
      this.socialSummary = await this.restService.getSocialSummary(sessionId);
      this.friends = await this.restService.getFriends(sessionId);
      this.friendRequests = await this.restService.getFriendRequests(sessionId);
      this.suggestions = await this.restService.getFriendSuggestions(sessionId);
    } catch (error) {
      LoggerService.error('Failed to load study together hub:', error);
    } finally {
      this.friendsLoading = false;
    }
  }

  async checkFindUsername(): Promise<void> {
    const username = this.findUsername.trim();
    if (!username) {
      this.findCheckMessage = 'Enter a username.';
      this.findCheckOk = false;
      return;
    }
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      return;
    }
    this.findCheckLoading = true;
    this.findCheckMessage = '';
    try {
      const found = await this.restService.checkFriendUsername(sessionId, username);
      this.findCheckOk = found;
      this.findCheckMessage = found
        ? 'Pilot found. You can send a friend request.'
        : 'Username not found or that account cannot receive friend requests.';
    } finally {
      this.findCheckLoading = false;
    }
  }

  async sendFindRequest(): Promise<void> {
    const username = this.findUsername.trim();
    if (!username) {
      return;
    }
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      return;
    }
    this.findSubmitLoading = true;
    try {
      const result = await this.restService.sendFriendRequest(sessionId, username);
      if (result.ok) {
        this.findCheckMessage = 'Friend request sent.';
        this.findCheckOk = false;
        this.findUsername = '';
        await this.loadHubData();
      } else {
        this.findCheckMessage = result.error || 'Failed to send request.';
        this.findCheckOk = false;
      }
    } finally {
      this.findSubmitLoading = false;
    }
  }

  async inviteSuggestion(userId: number, displayName: string): Promise<void> {
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      return;
    }
    this.inviteLoadingUserId = userId;
    try {
      const result = await this.restService.sendFriendRequest(sessionId, displayName);
      if (result.ok) {
        await this.loadHubData();
      }
    } finally {
      this.inviteLoadingUserId = null;
    }
  }

  async acceptRequest(friendshipId: number): Promise<void> {
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      return;
    }
    if (await this.restService.acceptFriendRequest(sessionId, friendshipId)) {
      await this.loadHubData();
    }
  }

  async declineRequest(friendshipId: number): Promise<void> {
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      return;
    }
    if (await this.restService.declineFriendRequest(sessionId, friendshipId)) {
      await this.loadHubData();
    }
  }

  async cancelRequest(friendshipId: number): Promise<void> {
    const sessionId = this.authService.getUser()?.sid;
    if (!sessionId) {
      return;
    }
    if (await this.restService.cancelFriendRequest(sessionId, friendshipId)) {
      await this.loadHubData();
    }
  }

  incomingRequests(): FriendRequestSummary[] {
    return this.friendRequests.filter(r => r.direction === 'incoming');
  }

  outgoingRequests(): FriendRequestSummary[] {
    return this.friendRequests.filter(r => r.direction === 'outgoing');
  }

  sharedBadgeCount(): number {
    return this.socialSummary?.sharedTestsCount ?? 0;
  }

  friendsBadgeCount(): number {
    return this.socialSummary?.pendingFriendRequests ?? 0;
  }

  hasSharedTestNotification(): boolean {
    return !!this.socialSummary?.sharedTestNotificationTitle;
  }

  suggestionLabel(s: FriendSuggestion): string {
    if (s.authorityName) {
      return `${s.displayName} · ${s.authorityName}`;
    }
    if (s.countryName) {
      return `${s.displayName} · ${s.countryName}`;
    }
    return s.displayName;
  }
}
